import { useCallback, useEffect, useRef, useState } from "react";

import { getAttendanceDeviceData } from "components/attendance/helpers";
import useAttendanceMutationScan from "./attendance-use-mutation-scan";


export const ATTENDANCE_SCAN_STATES = {
    IDLE: "idle",
    SCANNING: "scanning",
    LOCATING: "locating",
    SUBMITTING: "submitting",
    RESULT: "result",
    FAILED: "failed"
};

export const ATTENDANCE_ACTIONS = {
    CHECK_IN: "check_in",
    CHECK_OUT: "check_out"
};

const GEOLOCATION_OPTIONS = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0
};

const QR_SCANNER_CONFIG = {
    fps: 10,
    qrbox: {
        width: 240,
        height: 240
    }
};

const FALLBACK_CAMERA_CONFIG = { facingMode: "environment" };

const REAR_CAMERA_LABEL_PATTERN = /back|rear|environment|world|zad|tyl|spate|arriere|rueck|ruck/i;

const getReaderId = () => `attendance-reader-${ Date.now() }-${ Math.random().toString(16).slice(2) }`;

const waitForReaderElement = (readerId, timeout = 2000) => new Promise((resolve) => {
    const startedAt = Date.now();

    const check = () => {
        const readerElement = document.getElementById(readerId);

        if (readerElement) {
            resolve(readerElement);

            return;
        }

        if (Date.now() - startedAt >= timeout) {
            resolve(null);

            return;
        }

        window.requestAnimationFrame(check);
    };

    check();
});

const getSecureContextFailure = () => ({
    reason: "insecure_context",
    title: "ATTENDANCE.Error_SecureContext_Title",
    text: "ATTENDANCE.Error_SecureContext_Text"
});

const isSecureBrowserContext = () => (
    typeof window === "undefined" || window.isSecureContext !== false
);

const loadHtml5Qrcode = async () => {
    const module = await import("html5-qrcode");

    return module.Html5Qrcode;
};

const normalizeResult = (payload) => ({
    ...payload,
    result: String(payload?.result || "").toLowerCase(),
    reason: String(payload?.reason || "").toLowerCase()
});

const getErrorMessage = (error) => {
    if (!error) {
        return "";
    }

    if (typeof error === "string") {
        return error;
    }

    return error.message || error.name || String(error);
};

const isCameraPermissionError = (error) => (
    /notallowed|permission|denied|securityerror/i.test(getErrorMessage(error))
);

const isCameraNotFoundError = (error) => (
    /notfound|overconstrained|constraint|no camera|requested device not found/i.test(getErrorMessage(error))
);

const getCameraFailure = (error) => {
    if (!window.navigator?.mediaDevices?.getUserMedia) {
        return {
            reason: "camera_unsupported",
            title: "ATTENDANCE.Error_Camera_Title",
            text: "ATTENDANCE.Error_Camera_Text"
        };
    }

    if (isCameraPermissionError(error)) {
        return {
            reason: "camera_permission_denied",
            title: "ATTENDANCE.Error_CameraPermission_Title",
            text: "ATTENDANCE.Error_CameraPermission_Text"
        };
    }

    if (isCameraNotFoundError(error)) {
        return {
            reason: "camera_not_found",
            title: "ATTENDANCE.Error_CameraNotFound_Title",
            text: "ATTENDANCE.Error_CameraNotFound_Text"
        };
    }

    return {
        reason: "camera_unavailable",
        title: "ATTENDANCE.Error_Camera_Title",
        text: "ATTENDANCE.Error_Camera_Text"
    };
};

const getCameraStartConfigs = async (Html5Qrcode) => {
    let cameras = [];

    try {
        cameras = await Html5Qrcode.getCameras();
    } catch (e) {
        if (isCameraPermissionError(e) || isCameraNotFoundError(e)) {
            throw e;
        }

        // Enumeration can be unreliable on mobile Safari; still try facingMode.
        return [ FALLBACK_CAMERA_CONFIG ];
    }

    if (!Array.isArray(cameras) || cameras.length === 0) {
        return [ FALLBACK_CAMERA_CONFIG ];
    }

    const rearCamera = cameras.find((camera) => (
        REAR_CAMERA_LABEL_PATTERN.test(camera?.label || "")
    ));
    const firstCamera = cameras.find((camera) => camera?.id && camera.id !== rearCamera?.id);

    return [
        rearCamera?.id,
        FALLBACK_CAMERA_CONFIG,
        firstCamera?.id
    ].filter(Boolean);
};

const startScannerWithConfigs = async (scanner, configs, onScanSuccess) => {
    let lastError = null;

    for (const cameraConfig of configs) {
        try {
            await scanner.start(
                cameraConfig,
                QR_SCANNER_CONFIG,
                onScanSuccess
            );

            return;
        } catch (e) {
            lastError = e;

            if (isCameraPermissionError(e)) {
                throw e;
            }
        }
    }

    throw lastError || new Error("Camera unavailable");
};

const getGeolocationFailure = (error) => {
    if (error?.code === 1) {
        return {
            reason: "permission_denied",
            title: "ATTENDANCE.Error_GpsPermission_Title",
            text: "ATTENDANCE.Error_GpsPermission_Text"
        };
    }

    if (error?.code === 3) {
        return {
            reason: "gps_timeout",
            title: "ATTENDANCE.Error_GpsTimeout_Title",
            text: "ATTENDANCE.Error_GpsTimeout_Text"
        };
    }

    return {
        reason: "gps_unavailable",
        title: "ATTENDANCE.Error_GpsUnavailable_Title",
        text: "ATTENDANCE.Error_GpsUnavailable_Text"
    };
};

const useAttendanceScan = ({ action, eeId }) => {
    const readerIdRef = useRef(getReaderId());
    const scannerRef = useRef(null);
    const scannerRunningRef = useRef(false);
    const scanLockedRef = useRef(false);
    const mountedRef = useRef(false);

    const [ scanState, setScanState ] = useState(ATTENDANCE_SCAN_STATES.IDLE);
    const [ result, setResult ] = useState(null);
    const [ failure, setFailure ] = useState(null);

    const { attendanceScan } = useAttendanceMutationScan();

    const setStateIfMounted = useCallback((callback) => {
        if (mountedRef.current) {
            callback();
        }
    }, []);

    const stopScanner = useCallback(async () => {
        const scanner = scannerRef.current;

        if (!scanner) {
            return;
        }

        if (scannerRunningRef.current) {
            try {
                await scanner.stop();
            } catch (e) {
                // The scanner can already be stopped by the browser permission flow.
            }
        }

        try {
            await scanner.clear();
        } catch (e) {
            // Clearing is best-effort; the instance is discarded below either way.
        }

        scannerRunningRef.current = false;
        scannerRef.current = null;
    }, []);

    const submitScan = useCallback(async (qrPayload, position) => {
        const { latitude, longitude, accuracy } = position.coords || {};

        setStateIfMounted(() => {
            setScanState(ATTENDANCE_SCAN_STATES.SUBMITTING);
        });

        try {
            const { data } = await attendanceScan({
                ee_id: eeId,
                action,
                qr_payload: qrPayload,
                latitude,
                longitude,
                accuracy,
                ...getAttendanceDeviceData()
            });

            const scanPayload = data?.attendanceScan;

            if (!scanPayload) {
                throw new Error("Empty attendance scan payload");
            }

            setStateIfMounted(() => {
                setResult(normalizeResult(scanPayload));
                setScanState(ATTENDANCE_SCAN_STATES.RESULT);
            });
        } catch (e) {
            setStateIfMounted(() => {
                setFailure({
                    reason: "server_error",
                    title: "ATTENDANCE.Error_Server_Title",
                    text: "ATTENDANCE.Error_Server_Text"
                });
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });
        }
    }, [
        action,
        attendanceScan,
        eeId,
        setStateIfMounted
    ]);

    const requestLocation = useCallback((qrPayload) => {
        setStateIfMounted(() => {
            setScanState(ATTENDANCE_SCAN_STATES.LOCATING);
        });

        if (!isSecureBrowserContext()) {
            setStateIfMounted(() => {
                setFailure(getSecureContextFailure());
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });

            return;
        }

        if (!window.navigator?.geolocation) {
            setStateIfMounted(() => {
                setFailure({
                    reason: "gps_unsupported",
                    title: "ATTENDANCE.Error_GpsUnavailable_Title",
                    text: "ATTENDANCE.Error_GpsUnavailable_Text"
                });
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });

            return;
        }

        window.navigator.geolocation.getCurrentPosition(
            (position) => submitScan(qrPayload, position),
            (error) => {
                setStateIfMounted(() => {
                    setFailure(getGeolocationFailure(error));
                    setScanState(ATTENDANCE_SCAN_STATES.FAILED);
                });
            },
            GEOLOCATION_OPTIONS
        );
    }, [
        setStateIfMounted,
        submitScan
    ]);

    const handleScanSuccess = useCallback((decodedText) => {
        if (scanLockedRef.current) {
            return;
        }

        scanLockedRef.current = true;

        stopScanner().finally(() => {
            requestLocation(decodedText);
        });
    }, [
        requestLocation,
        stopScanner
    ]);

    const startScanner = useCallback(async () => {
        scanLockedRef.current = false;

        setResult(null);
        setFailure(null);

        if (!isSecureBrowserContext()) {
            setFailure(getSecureContextFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        if (!window.navigator?.mediaDevices?.getUserMedia) {
            setFailure(getCameraFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        const readerElement = await waitForReaderElement(readerIdRef.current);

        if (!mountedRef.current) {
            return;
        }

        if (!readerElement) {
            setFailure(getCameraFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        await stopScanner();

        const Html5Qrcode = await loadHtml5Qrcode();

        if (!mountedRef.current || !document.getElementById(readerIdRef.current)) {
            return;
        }

        const scanner = new Html5Qrcode(readerIdRef.current, false);

        scannerRef.current = scanner;
        setScanState(ATTENDANCE_SCAN_STATES.SCANNING);

        try {
            const cameraConfigs = await getCameraStartConfigs(Html5Qrcode);

            await startScannerWithConfigs(scanner, cameraConfigs, handleScanSuccess);

            scannerRunningRef.current = true;
        } catch (e) {
            try {
                await scanner.clear();
            } catch (clearError) {
                // The scanner may have failed before creating DOM children.
            }

            scannerRef.current = null;
            scannerRunningRef.current = false;

            setFailure(getCameraFailure(e));
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);
        }
    }, [
        handleScanSuccess,
        stopScanner
    ]);

    const retry = useCallback(() => {
        startScanner();
    }, [ startScanner ]);

    const reset = useCallback(() => {
        scanLockedRef.current = false;
        setResult(null);
        setFailure(null);
        setScanState(ATTENDANCE_SCAN_STATES.IDLE);
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            stopScanner();
        };
    }, [ stopScanner ]);

    return {
        readerId: readerIdRef.current,
        scanState,
        result,
        failure,
        startScanner,
        stopScanner,
        retry,
        reset
    };
};

export default useAttendanceScan;
